import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAffirmBnplHasValidAmount } from './useAffirmBnplHasValidAmount';
import { useAffirmBnplPromoSync } from './useAffirmBnplPromoSync';

import Card from 'components/Shared/Card/Card';
import { Check } from 'components/Shared/Icons/Icons';
import SegmentIO from 'reporting/SegmentIO';
import { colors, paddingSize, borderRadius } from 'styles/cp';
type PayOverTimeCardProps = {
  amountCents: number;
  isActive: boolean;
};

export default function PayOverTimeCard({ amountCents, isActive }: PayOverTimeCardProps) {
  const intl = useIntl();
  const affirmBnplAsLowAsRef = React.useRef<HTMLParagraphElement | null>(null);
  const isAmountValidForAffirmBnpl = useAffirmBnplHasValidAmount();
  // Sync Affirm promo message elements to avoid duplicate API calls
  useAffirmBnplPromoSync(affirmBnplAsLowAsRef);

  return (
    <section className="affirm-bnpl-card" aria-hidden={!isActive} hidden={!isActive}>
      <Card
        padding={`${paddingSize.lg}`}
        bgColor={colors.stoneWhite01}
        borderRadius={borderRadius.default}
      >
        <h2 className="affirm-title">
          {intl.formatMessage({
            id: 'AFFIRM_PAY_OVER_TIME_TITLE',
            defaultMessage: 'Pay your invoice over time through Affirm',
          })}
        </h2>

        <div
          className="affirm-bnpl-pill"
          aria-hidden={!isAmountValidForAffirmBnpl}
          hidden={!isAmountValidForAffirmBnpl}
        >
          <span>
            <p
              id="affirm-as-low-as-pay-over-time"
              ref={affirmBnplAsLowAsRef}
              data-page-type="payment"
              data-amount={String(amountCents)}
              onClick={useCallback(() => SegmentIO.affirmBnplPromoClicked('pay_over_time'), [])}
            />
          </span>
        </div>

        <ul className="affirm-bnpl-list">
          <li>
            <Check width={18} height={18} color={colors.green} aria-label="check" />
            <span>
              {intl.formatMessage({
                id: 'AFFIRM_NO_LATE_FEES',
                defaultMessage: 'No late fees',
              })}
            </span>
          </li>
          <li>
            <Check width={18} height={18} color={colors.green} aria-label="check" />
            <span>
              {intl.formatMessage({
                id: 'AFFIRM_SEE_PLANS_IN_SECONDS',
                defaultMessage: 'See your plans in seconds',
              })}
            </span>
          </li>
          <li>
            <Check width={18} height={18} color={colors.green} aria-label="check" />
            <span className="affirm-disclaimer">
              {intl.formatMessage({
                id: 'AFFIRM_ELIGIBILITY_NOTE',
                defaultMessage: 'Checking eligibility won’t impact your credit',
              })}
            </span>
          </li>
        </ul>
      </Card>
      <style>{`
        .affirm-bnpl-card {
          width: 100%;
        }

        /* Title */
        .affirm-title {
          margin: 0 0 14px 0;
          font-family: AvenirNextforINTUIT-Demi;
          font-size: 16px;
          font-weight: 600; /* Demi/Semibold */
          font-style: normal;
          line-height: 150%;
          letter-spacing: 0;
          color: var(--Text-Primary, ${colors.gray});
        }

        /* Safari-only: font-weight 600 renders too heavy, use 400 instead */
        @supports (-webkit-hyphens: none) {
          .affirm-title {
            font-weight: 400;
          }
        }

        /* Pill */
        .affirm-bnpl-pill[hidden] {
          display: none !important;
        }

        .affirm-bnpl-pill {
          display: inline-flex;
          margin-bottom: ${paddingSize.sm};
        }

        #affirm-as-low-as-pay-over-time { margin: 0; }

        /* List */
        .affirm-bnpl-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          gap: 10px;
        }

        .affirm-bnpl-list li {
          display: grid;
          grid-template-columns: 22px 1fr;
          align-items: start;
          column-gap: 5px;
          font-family: AvenirNextforINTUIT-Regular;
          font-weight: 400;
          font-style: normal;
          font-size: 14px;
          line-height: 130%;
          letter-spacing: 0;
          color: var(--Text-Primary, ${colors.gray});
        }

        .affirm-disclaimer {
          font-size: inherit;
          color: inherit;
        }
      `}</style>
    </section>
  );
}
